import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Premium corporate loading splash.
 *
 * Aesthetic: restrained, editorial, engineering-grade.
 * No bouncing, no sparks, no theatrics. Subtle motion only.
 *
 * Choreography (~2.4s):
 *  1. Crisp white stage materialises (no curtain, no bloom).
 *  2. A single hairline rule draws horizontally across the centre.
 *  3. Logo fades up (opacity + 8px Y) with a 1.04 → 1.00 settle.
 *  4. Underline rule extends and an amber tick appears at its right end.
 *  5. Section meta-text fades in (Engineering · Manufacturing · Export).
 *  6. Thin progress bar fills in cubic-ease.
 *  7. Splash dismisses with a brief opacity + 2px Y exit.
 */

const EASE = [0.22, 1, 0.36, 1];

export default function LoadingScreen() {
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const TARGET_MIN = 2200;
    const tick = (now) => {
      const elapsed = now - start;
      const p = Math.min(1, elapsed / TARGET_MIN);
      const eased = 1 - Math.pow(1 - p, 3);
      setProgress(eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const finish = () => setTimeout(() => setDone(true), 2300);
    if (document.readyState === "complete") finish();
    else window.addEventListener("load", finish, { once: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("load", finish);
    };
  }, []);

  useEffect(() => {
    if (!done) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [done]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="loading"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: -2, transition: { duration: 0.55, ease: EASE } }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-white"
          data-testid="loading-screen"
        >
          {/* Subtle blueprint grid — barely there */}
          <div className="absolute inset-0 bp-grid-dark opacity-[0.06] pointer-events-none" />

          {/* Horizontal hairline drawing across the page */}
          <motion.div
            aria-hidden
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ duration: 0.95, ease: EASE, delay: 0.05 }}
            className="absolute left-0 right-0 top-1/2 -translate-y-[2px] h-px bg-gradient-to-r from-transparent via-brand-navy/20 to-transparent origin-center"
          />

          <div className="relative flex flex-col items-center px-6">
            {/* Logo — refined fade-up, no rotation, no bounce */}
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 1.04 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.0, delay: 0.45, ease: EASE }}
              className="relative"
            >
              <img
                src="/logo-v2.png"
                alt="Tirupati Global Sourcing Company"
                className="h-20 sm:h-24 lg:h-28 w-auto object-contain select-none"
                style={{ filter: "drop-shadow(0 14px 36px rgba(11,46,89,0.12))" }}
              />
            </motion.div>

            {/* Underline rule + amber tick */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 1.15 }}
              className="mt-7 relative flex items-center justify-center"
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: 144 }}
                transition={{ duration: 0.7, delay: 1.2, ease: EASE }}
                className="h-px bg-brand-navy/35"
              />
              <motion.span
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 1.85, ease: EASE }}
                className="ml-3 inline-block h-1.5 w-1.5 bg-brand-orange"
                aria-hidden
              />
            </motion.div>

            {/* Tagline — single line, restrained */}
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 1.35, ease: EASE }}
              className="mt-5 text-[10.5px] uppercase tracking-[0.42em] text-brand-text/55 font-semibold"
            >
              Engineering · Manufacturing · Export
            </motion.div>

            {/* Thin progress bar — sober, no sweep */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 1.5 }}
              className="mt-10 w-56 h-px bg-brand-navy/12 relative overflow-hidden"
            >
              <div
                className="absolute inset-y-0 left-0 bg-brand-navy transition-[width] duration-100"
                style={{ width: `${Math.min(100, progress * 100)}%` }}
              />
            </motion.div>

            {/* Certification footer — kerned wide */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.75 }}
              className="mt-10 flex items-center gap-5 text-[9.5px] uppercase tracking-[0.36em] text-brand-text/40"
            >
              <span>ISO 9001:2015</span>
              <span className="h-px w-3 bg-brand-text/25" />
              <span>IATF 16949:2016</span>
              <span className="h-px w-3 bg-brand-text/25" />
              <span>VDA 6.3</span>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
